/* eslint-disable security/detect-non-literal-fs-filename */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom, firstValueFrom } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { MessageRejectedException } from 'src/app/shared/errors/cognito/cognito-errors';
import {
  BusinessFrozenException,
  CannotInviteToInactiveBusinessException,
  RepresentativeLimitReachedException,
} from 'src/app/shared/errors/user-management-service/user-management-service-errors';
import { BusinessRepresentativeInteraction } from 'src/app/shared/models/constants/business-representative-interaction';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { DataUpdateFeedback } from 'src/app/shared/models/data-update-feedback/data-update-feedback';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { InviteRepresentativeResponseDTO } from 'src/app/shared/models/dtos/responses/invite-representative-response-dto/invite-representative-response-dto';
import {
  GeneralizedRepresentative,
  isRepresentative,
} from 'src/app/shared/models/generalized-representative/generalized-representative';
import { RepresentativeItemLabels } from 'src/app/shared/models/representative-item-labels/representative-item-labels';
import {
  emailPattern,
  notInValidator,
} from 'src/app/shared/validators/registration-validators';
import {
  DialogModalComponent,
  SecondaryContent,
} from '../../molecules/dialog-modal/dialog-modal.component';
import { InviteRepresentativeRequestDTO } from 'src/app/shared/models/dtos/requests/invite-representative-request-dto/invite-representative-request-dto';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { RepresentativeRole } from 'src/app/shared/models/dtos/register-form/representative/representative-role';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { BusinessStatus } from 'src/app/shared/models/constants/business-status';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';

@Component({
  selector: 'app-representative-invite-list',
  templateUrl: './representative-invite-list.component.html',
  styleUrls: ['./representative-invite-list.component.scss'],
})
/**
 * Accordeon item that show business representatives
 */
export class RepresentativeInviteListComponent implements OnInit {
  @Input() businessId!: number;
  @Input() businessStatus!: BusinessStatus;
  @Input() isSummary?: boolean;
  @Input() isBusinessActive?: boolean = true;
  representativeList: Representative[] = [];
  isLastRepresentative: boolean = false;
  inviteeList: InviteRepresentativeResponseDTO[] = [];
  @Output() nRepresentativeEvent = new EventEmitter<number>();
  labels = {
    invitationExpired: 'invitation-expired',
    emailAddress: 'email-address',
    inviteBusinessRepresentative: 'invite-business-representative',
    inviteABusinessRepresentative: 'invite-a-business-representative',
    businessRepresentatives: 'business-representatives',
    removeRepresentative: 'confirm-delete-user',
    removeLastRepresentative: 'last-user-business-removal',
    disableBusinessWarning: 'disable-business-warning',
    activateBusinessInfoUser: 'activate-business-info-user',
    removeUser: 'remove-user',
    userRemoval: 'user-removal',
    ok: 'ok',
    addABusinessRepresentative: 'add-a-business-representative',
    printerRequiredForInvitation: 'printer-required-for-invitation',
    addAValidPrinter: 'add-a-valid-printer',
    businessRepresentativeInvited: 'business-representative-invited',
    businessReactivationMessage: 'business-reactivation-message',
    businessRemainsDisabled: 'business-remains-disabled',
    businessFrozenException: 'business-is-frozen-exception',
    activePointsOrCouponsRestored: 'active-points-or-coupons-restored',
    unexpectedError: 'unexpected-error',
  };
  representativeItemLabels = new RepresentativeItemLabels(
    'promote',
    'delete',
    'resend-invite',
    'no-representatives-found'
  );
  private promoteDialogData = {
    content: 'confirm-promote-user-to-admin',
    successButtonText: 'confirm',
    failButtonText: 'cancel',
  };
  private deleteRepresentativeDialogData = {
    content: this.labels.removeRepresentative,
    failButtonText: 'cancel',
    secondaryContent: [] as SecondaryContent[],
    deleteButtonText: this.labels.removeUser,
    title: this.labels.userRemoval,
  };
  private deleteInviteDialogData = {
    ...this.promoteDialogData,
    content: 'confirm-delete-invite',
  };
  private resendInviteDialogData = {
    ...this.promoteDialogData,
    content: 'confirm-resend-invite',
  };
  private cannotInviteDialogData = {
    title: this.labels.addABusinessRepresentative,
    content: this.labels.printerRequiredForInvitation,
    secondaryContent: this.getCannotInviteSecondaryContent(),
    successButtonText: this.labels.ok,
  };
  private inviteToInactiveBusinessDialogData = {
    title: this.labels.businessRepresentativeInvited,
    content: this.labels.businessReactivationMessage,
    secondaryContent: this.getInviteSecondaryContent(),
    successButtonText: this.labels.ok,
  };
  manageRepresentatives!: UntypedFormGroup;
  inviteLoading: boolean = false;
  listLoading: boolean = false;
  inviteErrorFeedback = new DataUpdateFeedback();
  isSingleAdmin: boolean = false;
  disabledBusiness: boolean = false;
  private duplicatedEmaildialogData = {
    title: '',
    content: '',
  };

  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    public dialog: MatDialog,
    private translationService: TranslationService,
    private dialogService: DialogService,
    private authenticatedUserService: AuthenticatedUserService,
    private snackBarService: SnackBarService
  ) {}

  /**
   * Init
   */
  ngOnInit() {
    this.manageRepresentatives = new UntypedFormGroup({
      email: new UntypedFormControl('', [
        Validators.required,
        Validators.email,
        emailPattern(),
        notInValidator(
          this.representativeList,
          (el: Representative) => el.email || ''
        ),
      ]),
    });
    this.translationService.translateObject(this.labels);
    this.translationService.translateObject(this.representativeItemLabels);

    this.updateRepresentativesAndInvitees();

    this.translationService.translateObject(this.promoteDialogData);
    this.translationService.translateObject(
      this.deleteRepresentativeDialogData
    );
    this.translationService.translateObject(this.deleteInviteDialogData);
    this.translationService.translateObject(this.resendInviteDialogData);
    this.translationService.translateObject(this.cannotInviteDialogData);
    this.translationService.translateObject(
      this.inviteToInactiveBusinessDialogData
    );
  }

  /**
   * Checks if there is only one representative and
   * updates dialogData content and secondary content
   * for the last representative deletion scenario.
   * @return {Promise<void>} Promise<void>
   */
  private async updateDeleteRepresentativeDialogDataContent(): Promise<void> {
    this.deleteRepresentativeDialogData['content'] = this.isLastRepresentative
      ? this.labels.removeLastRepresentative
      : this.labels.removeRepresentative;

    this.deleteRepresentativeDialogData['secondaryContent'] = this
      .isLastRepresentative
      ? await this.getDeleteSecondaryContent()
      : [];
  }

  /**
   * Sends total number of representatives
   */
  representativeCounter() {
    this.nRepresentativeEvent.emit(this.representativeList.length);
  }

  /**
   * If there is only an admin
   */
  isSingleCheck(): void {
    const list = [];
    list.push(this.representativeList);
    list.push(this.inviteeList);

    if (list[0].length === 1 && list[1].length === 0) {
      this.isSingleAdmin = true;
    } else if (list.length === 0) {
      this.disabledBusiness = true;
    }
  }

  /**
   * Open confirm promotion modal
   * @param {Representative} representative
   */
  async openConfirmPromotionDialog(representative: Representative) {
    if (representative.id == null) {
      throw new Error('representative id must be present');
    }
    const name = `${representative.firstName} ${representative.lastName}`;
    const dialogResponse = await this.openDialog(
      BusinessRepresentativeInteraction.PROMOTE
    )(name);
    if (dialogResponse === MatDialogResponse.SUCCESS) {
      this.listLoading = true;
      this.userService.promoteRepresentative(representative.id).subscribe({
        next: () => {
          this.refreshRepresentativesList();
        },
        error: (err) => {
          if (err instanceof BusinessFrozenException) {
            this.snackBarService.error(this.labels.businessFrozenException);
          } else {
            this.snackBarService.error(this.labels.unexpectedError);
          }
          this.listLoading = false;
        },
      });
    }
  }

  /**
   * Refresh the representative list
   */
  async refreshRepresentativesList() {
    await this.updateRepresentativesAndInvitees();
    this.listLoading = false;
  }

  /**
   * Open confirm deletion modal
   * @param {GeneralizedRepresentative} generalizedRepresentative
   */
  async openConfirmDeleteDialog(
    generalizedRepresentative: GeneralizedRepresentative
  ) {
    if (isRepresentative(generalizedRepresentative)) {
      await this.openConfirmDeleteRepresentativeDialog(
        generalizedRepresentative
      );
    } else {
      await this.openConfirmDeleteInviteDialog(generalizedRepresentative);
    }
  }

  /**
   * Opens a dialog that will remove a representative
   * @param {Representative} representative
   */
  async openConfirmDeleteRepresentativeDialog(representative: Representative) {
    if (representative?.id == null) {
      throw new Error('representative id must be present');
    }
    const name = `${representative.firstName} ${representative.lastName}`;
    const dialogResponse = await this.openDialog(
      BusinessRepresentativeInteraction.DELETE_REPRESENTATIVE
    )(name);
    if (dialogResponse === MatDialogResponse.SUCCESS) {
      this.listLoading = true;
      this.userService.deleteRepresentative(representative.id).subscribe({
        next: () => {
          this.refreshRepresentativesList();
        },
        error: (err) => {
          if (err instanceof BusinessFrozenException) {
            this.snackBarService.error(this.labels.businessFrozenException);
          } else {
            this.snackBarService.error(this.labels.unexpectedError);
          }
          this.listLoading = false;
        },
      });
    }
  }

  /**
   * Opens confirm resend invite dialog
   * @param {InviteRepresentativeResponseDTO} invitee
   */
  async openConfirmResendInviteDialog(
    invitee: InviteRepresentativeResponseDTO
  ) {
    const dialogResponse = await this.openDialog(
      BusinessRepresentativeInteraction.RESEND_INVITE
    )(invitee.email);
    if (dialogResponse === MatDialogResponse.SUCCESS) {
      this.listLoading = true;
      await this.inviteBusinessRepresentative(invitee.email);
      await this.updateRepresentativesAndInvitees();
      this.listLoading = false;
    }
  }

  /**
   * Opens a dialog that will remove an invitation
   * @param {InviteRepresentativeResponseDTO} invitee
   */
  async openConfirmDeleteInviteDialog(
    invitee: InviteRepresentativeResponseDTO
  ) {
    const dialogResponse = await this.openDialog(
      BusinessRepresentativeInteraction.DELETE_INVITE
    )(invitee.email);
    if (dialogResponse === MatDialogResponse.SUCCESS) {
      this.listLoading = true;
      await this.confirmDeleteInvite(invitee.email);
      await this.updateRepresentativesAndInvitees();
      this.listLoading = false;
    }
  }

  /**
   * Send an invitation for a possible business representative
   * @param {string} emailToInvite
   */
  async inviteBusinessRepresentative(emailToInvite?: string) {
    let tookFromForm = false;
    let email: string;
    if (emailToInvite && emailToInvite.length > 0) {
      email = emailToInvite;
    } else if (this.manageRepresentatives.invalid) {
      throw new Error('invalid form');
    } else {
      email = this.manageRepresentatives.get('email')!.value;
      tookFromForm = true;
      const found = this.getGeneralizedRepresentativeList().some(
        (rep) => rep.email === email
      );
      if (found) {
        this.duplicatedEmaildialogData = {
          title: 'duplicated-email-address',
          content: 'existing-invitation',
        };
        this.translationService.translateObject(
          this.duplicatedEmaildialogData,
          {
            content: {
              email: email,
            },
          }
        );

        this.dialogService.openDialog(this.duplicatedEmaildialogData);
        return;
      }
    }
    this.inviteLoading = true;
    const inviteRepresentativeRequestDTO = new InviteRepresentativeRequestDTO(
      email,
      this.businessId
    );
    try {
      await lastValueFrom(
        this.userService.inviteRepresentative(inviteRepresentativeRequestDTO)
      );
      this.updateRepresentativesAndInvitees();
      if (tookFromForm) {
        const emailControl = this.manageRepresentatives.get('email')!;
        emailControl.reset('');
      }
      if (!this.isBusinessActive) {
        this.dialog.open(DialogModalComponent, {
          data: this.inviteToInactiveBusinessDialogData,
          autoFocus: false,
        });
      }
    } catch (error) {
      this.handleInviteError(error as Error);
    } finally {
      this.inviteLoading = false;
    }
  }

  /**
   * Updates both lists
   */
  async updateRepresentativesAndInvitees() {
    this.isSingleCheck();

    if (this.disabledBusiness) {
      this.listLoading = false;
    }

    this.listLoading = true;
    try {
      await this.pullInviteeList();
      await this.pullRepresentativeList();
      this.updateDeleteRepresentativeDialogDataContent();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }

      throw new Error(
        'An error occured while fetching Representatives and Invitees data'
      );
    } finally {
      this.listLoading = false;
    }
  }

  /**
   * Confirm delete invite action
   * @param {string} email
   */
  async confirmDeleteInvite(email: string) {
    await lastValueFrom(this.userService.deleteInvite(email));
  }

  /**
   * Handles possible errors in inviting a representative
   * @param {Error} error
   */
  private handleInviteError(error: Error) {
    let errorMsg = 'backend-comm-error';
    if (error instanceof MessageRejectedException) {
      errorMsg = 'invite-exception-message-rejected';
    } else if (error instanceof RepresentativeLimitReachedException) {
      errorMsg = 'invite-exception-rlr-001';
    } else if (error instanceof BusinessFrozenException) {
      errorMsg = 'business-is-frozen-exception';
    } else if (error instanceof CannotInviteToInactiveBusinessException) {
      this.dialog.open(DialogModalComponent, {
        data: this.cannotInviteDialogData,
        autoFocus: false,
      });
      return;
    }
    this.inviteErrorFeedback.setError(errorMsg);
  }

  /**
   * Checks if the invite button should be disabled
   * @return {boolean} True if the button should be disabled, false otherwise
   */
  isInviteRepresentativeButtonDisabled(): boolean {
    const isBusinessDisabled = this.businessStatus === BusinessStatus.DISABLED;
    const isViewOrCallCenterAgentAdmin =
      this.authenticatedUserService.isVOAdmin() ||
      this.authenticatedUserService.isCallCenterAgent();
    const isViewOnlyAdmin = this.authenticatedUserService.isVOAdmin();
    const isFormInvalid = this.manageRepresentatives.invalid;
    const isLoading = this.inviteLoading;

    return (
      (isBusinessDisabled && isViewOrCallCenterAgentAdmin) ||
      isViewOnlyAdmin ||
      isLoading ||
      isFormInvalid
    );
  }

  /**
   * Populates invitees list from backend
   */
  async pullInviteeList() {
    const inviteeList = await lastValueFrom(
      this.userService.listInvitees(this.businessId)
    );
    this.inviteeList.splice(0, this.inviteeList.length);
    this.inviteeList.push(...inviteeList);
    this.isSingleCheck();
    this.representativeCounter();
  }

  /**
   * Populates representative list from backend
   */
  async pullRepresentativeList() {
    const representativeList = await lastValueFrom(
      this.userService.listRepresentatives(this.businessId)
    );
    const activeRepresentativeList = representativeList.filter(
      (r) => r.active === true
    );
    this.representativeList.splice(0, this.representativeList.length);
    this.representativeList.push(...activeRepresentativeList);
    this.representativeList.sort((a, b) => {
      // Sorts representative list by role
      if (a.role === RepresentativeRole.BUSINESS_ADMIN) return -1;
      if (b.role === RepresentativeRole.BUSINESS_ADMIN) return 1;
      return 0;
    });
    this.isLastRepresentative = this.representativeList.length === 1;
    this.isSingleCheck();
    this.representativeCounter();
  }

  /**
   * Joins representatives and invitees into a generalized list
   * @return {GeneralizedRepresentative[]}
   */
  getGeneralizedRepresentativeList(): GeneralizedRepresentative[] {
    const generalizedRepresentativeList: GeneralizedRepresentative[] = [];
    generalizedRepresentativeList.push(...this.representativeList);
    generalizedRepresentativeList.push(...this.inviteeList);
    return generalizedRepresentativeList;
  }

  /**
   * @param {BusinessRepresentativeInteraction} interaction
   * @return {function(string): Promise<MatDialogResponse>}
   */
  openDialog(
    interaction: BusinessRepresentativeInteraction
  ): (username: string) => Promise<MatDialogResponse> {
    return (username: string) => {
      let dialogData: any;
      if (interaction === BusinessRepresentativeInteraction.PROMOTE) {
        dialogData = this.promoteDialogData;
      } else if (
        interaction === BusinessRepresentativeInteraction.DELETE_REPRESENTATIVE
      ) {
        dialogData = this.deleteRepresentativeDialogData;
      } else if (
        interaction === BusinessRepresentativeInteraction.DELETE_INVITE
      ) {
        dialogData = this.deleteInviteDialogData;
      } else if (
        interaction === BusinessRepresentativeInteraction.RESEND_INVITE
      ) {
        dialogData = this.resendInviteDialogData;
      }

      const dialogRef = this.dialog.open(DialogModalComponent, {
        data: {
          ...dialogData,
          content: dialogData.content.replace('[user]', username),
        },
        autoFocus: false,
      });
      return firstValueFrom<MatDialogResponse>(dialogRef.afterClosed());
    };
  }

  /**
   * @param {MatSelectChange} event
   *
   */
  private async getDeleteSecondaryContent(): Promise<SecondaryContent[]> {
    // TODO refactor styles so its less verbose
    return [
      {
        content: await this.translationService.translateString(
          this.labels.disableBusinessWarning
        ),
        style: {
          color: 'var(--Cinzas-400, #19212B)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
        },
      },
      {
        content: await this.translationService.translateString(
          this.labels.activateBusinessInfoUser
        ),
        style: {
          color: 'var(--Cinzas-300, #4E4E4E)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '700',
          lineHeight: '125%',
        },
      },
    ];
  }

  /**
   * @param {MatSelectChange} event
   *
   */
  private async getCannotInviteSecondaryContent(): Promise<SecondaryContent[]> {
    // TODO refactor styles so its less verbose
    return [
      {
        content: await this.translationService.translateString(
          this.labels.addAValidPrinter
        ),
        style: {
          color: 'var(--Cinzas-400, #19212B)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
        },
      },
    ];
  }

  /**
   * @param {MatSelectChange} event
   *
   */
  private async getInviteSecondaryContent(): Promise<SecondaryContent[]> {
    // TODO refactor styles so its less verbose
    return [
      {
        content: await this.translationService.translateString(
          this.labels.businessRemainsDisabled
        ),
        style: {
          color: 'var(--Cinzas-400, #19212B)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
        },
      },
      {
        content: await this.translationService.translateString(
          this.labels.activePointsOrCouponsRestored
        ),
        style: {
          color: 'var(--Cinzas-300, #4E4E4E)',
          fontFamily: 'Helvetica',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: '700',
          lineHeight: '125%',
        },
      },
    ];
  }
}
